import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useAdaptiveAnimation } from '../../../hooks/useAdaptiveAnimation';
import { cn } from '../../../lib/utils';

/**
 * RouteTransition component props interface
 * Defines the structure for route-based transition animations
 */
export interface RouteTransitionProps {
  /** Child components (page content) to animate */
  children: React.ReactNode;
  /** Custom CSS classes for the container */
  className?: string;
  /** Animation duration override */
  duration?: number;
  /** Whether to disable transitions */
  disableTransitions?: boolean;
}

/**
 * RouteTransition Component - Route-Based Transition System
 *
 * Advanced transition system that detects route changes and applies
 * appropriate animations based on navigation patterns and user preferences.
 *
 * Features:
 * - Automatic route change detection
 * - Context-aware animation directions
 * - Adaptive animation system integration
 * - Reduced motion support
 * - Hardware-accelerated animations
 * - Seamless integration with React Router
 *
 * @param props - RouteTransition component properties
 * @returns JSX.Element - Route-aware animated container
 */
export const RouteTransition: React.FC<RouteTransitionProps> = ({
  children,
  className,
  duration,
  disableTransitions = false,
}) => {
  // Simplified version for debugging - just render children without complex animations
  if (disableTransitions) {
    return <div className={className}>{children}</div>;
  }
  
  const location = useLocation();
  const { deviceTier } = useAdaptiveAnimation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const isTransitioningRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check for reduced motion preference
  const reducedMotion = typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Determine animation direction based on route patterns
  const getTransitionDirection = useCallback((): 'left' | 'right' | 'fade' => {
    const currentPath = displayLocation.pathname;
    const nextPath = location.pathname;

    // Define route hierarchy for directional animations
    const routeHierarchy: Record<string, number> = {
      '/': 0,
      '/about-us': 1,
      '/blog': 2,
      '/contact': 3,
      '/login': 4,
      '/register': 5,
    };

    const currentLevel = routeHierarchy[currentPath] ?? 0;
    const nextLevel = routeHierarchy[nextPath] ?? 0;

    if (nextLevel > currentLevel) return 'right'; // Forward navigation
    if (nextLevel < currentLevel) return 'left';  // Backward navigation
    return 'fade'; // Same level or unknown routes
  }, [displayLocation.pathname, location.pathname]);

  // Define animation variants based on direction and device capabilities
  const getVariants = useCallback((): {
    initial: { opacity: number; x?: number };
    animate: { opacity: number; x?: number };
    exit: { opacity: number; x?: number };
  } => {
    const direction = getTransitionDirection();
    const distance = deviceTier === 'low-end' || deviceTier === 'mobile' ? 15 : 25;

    switch (direction) {
      case 'left':
        return {
          initial: { opacity: 0, x: distance },
          animate: { opacity: 1, x: 0 },
          exit: { opacity: 0, x: -distance },
        };
      case 'right':
        return {
          initial: { opacity: 0, x: -distance },
          animate: { opacity: 1, x: 0 },
          exit: { opacity: 0, x: distance },
        };
      case 'fade':
      default:
        return {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
        };
    }
  }, [deviceTier, getTransitionDirection]);

  // Detect route changes and manage transition state
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (location.pathname !== displayLocation.pathname) {
      // Prevent multiple rapid transitions using ref instead of state
      if (isTransitioningRef.current) {
        console.warn('RouteTransition: Transition already in progress, skipping...');
        return;
      }

      isTransitioningRef.current = true;

      // Small delay to ensure component cleanup and prevent rapid transitions
      timeoutRef.current = setTimeout(() => {
        // Double-check that location hasn't changed again during the timeout
        if (location.pathname === displayLocation.pathname) {
          // Location hasn't changed, this was just a delay
          isTransitioningRef.current = false;
          timeoutRef.current = null;
          return;
        }

        setDisplayLocation(location);
        isTransitioningRef.current = false;
        timeoutRef.current = null;
      }, 50);
    }

    // Cleanup function
    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      isTransitioningRef.current = false;
    };
  }, [location.pathname, displayLocation.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Additional effect to handle rapid location changes
  useEffect(() => {
    // If location changes while transitioning, force update immediately
    if (isTransitioningRef.current && location.pathname !== displayLocation.pathname) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setDisplayLocation(location);
      isTransitioningRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]); // React Router location key changes on navigation - intentional limited deps

  // Skip animations if disabled or reduced motion is preferred
  if (disableTransitions || reducedMotion) {
    return (
      <div className={cn('w-full', className)}>
        {children}
      </div>
    );
  }

  const variants = getVariants();

  // Transition configuration
  const transition = {
    duration: duration || (deviceTier === 'low-end' || deviceTier === 'mobile' ? 0.25 : 0.4),
    ease: [0.25, 0.46, 0.45, 0.94] as const,
  };

  return (
    <div className={cn('relative w-full', className)}>
      <AnimatePresence
        mode="wait"
        initial={false}
      >
        <motion.div
          key={displayLocation.pathname}
          className="w-full"
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={transition}
          role="main"
          aria-live="polite"
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default RouteTransition;
